import { Controller } from "@hotwired/stimulus";
import { marked } from "marked";

// Configure marked for safe rendering
marked.setOptions({
  breaks: true, // Convert \n to <br>
  gfm: true, // GitHub Flavored Markdown
});

// Connects to data-controller="ai-assistant"
export default class extends Controller {
  static targets = ["collapsed", "inputBar", "conversation", "messages"];

  static values = {
    partyId: String,
  };

  connect() {
    this.state = "collapsed";
    this.messages = [];
  }

  openInput() {
    this.state = "input";
    this.render();
    setTimeout(() => {
      const input = this.inputBarTarget.querySelector("input");
      if (input) input.focus();
    }, 100);
  }

  openConversation() {
    this.state = "conversation";
    this.render();
    setTimeout(() => {
      const input = this.conversationTarget.querySelector("input");
      if (input) input.focus();
    }, 100);
  }

  minimize() {
    this.state = "input";
    this.render();
  }

  close() {
    this.state = "collapsed";
    this.render();
  }

  render() {
    this.collapsedTarget.style.display =
      this.state === "collapsed" ? "block" : "none";
    this.inputBarTarget.style.display =
      this.state === "input" ? "block" : "none";
    this.conversationTarget.style.display =
      this.state === "conversation" ? "block" : "none";
  }

  async sendMessage(event) {
    event.preventDefault();

    const input =
      this.state === "input"
        ? this.inputBarTarget.querySelector("input")
        : this.conversationTarget.querySelector("input");

    const message = input.value.trim();
    if (!message) return;

    // Add message to chat
    this.addMessage("user", message);
    input.value = "";

    // Switch to conversation view
    if (this.state === "input") {
      this.openConversation();
    }

    // Show loading
    this.showLoading();

    try {
      // Call backend API with optional party context
      const payload = { message };
      if (this.hasPartyIdValue && this.partyIdValue) {
        payload.party_id = this.partyIdValue;
      }

      const response = await fetch("/ai_assistant/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCSRFToken(),
        },
        body: JSON.stringify(payload),
      });

      this.hideLoading();

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = "Sorry, something went wrong.";
        try {
          const error = JSON.parse(errorText);
          errorMessage = error.error || errorMessage;
        } catch (e) {
          // Failed to parse error JSON, use default message
        }
        this.addMessage("assistant", errorMessage);
        return;
      }

      const data = await response.json();

      // Show tool executions if any
      if (data.tool_executions && data.tool_executions.length > 0) {
        this.showToolExecutions(data.tool_executions);
      }

      this.addMessage("assistant", data.content);
    } catch (error) {
      this.hideLoading();
      console.error("AI Assistant connection error:", error);
      this.addMessage(
        "assistant",
        "Sorry, I'm having trouble connecting. Please try again.",
      );
    }
  }

  getCSRFToken() {
    return document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content");
  }

  addMessage(role, content) {
    this.messages.push({ role, content });

    const div = document.createElement("div");
    div.className =
      role === "user" ? "flex justify-end mb-4" : "flex justify-start mb-4";

    const bubble = document.createElement("div");
    bubble.className = `max-w-[80%] px-5 py-3 rounded-radius shadow-default ${
      role === "user" ? "ai-chat-bubble-user" : "ai-chat-bubble-assistant"
    }`;

    if (role === "assistant") {
      const wrapper = document.createElement("div");
      wrapper.className = "flex items-start gap-3";

      const iconDiv = document.createElement("div");
      iconDiv.className = "shrink-0 mt-0.5";
      iconDiv.innerHTML = this.getSparklesIconSVG();

      const textDiv = document.createElement("div");
      textDiv.className = "text-sm font-body markdown-content";
      textDiv.innerHTML = marked.parse(content);

      wrapper.appendChild(iconDiv);
      wrapper.appendChild(textDiv);
      bubble.appendChild(wrapper);
    } else {
      const textDiv = document.createElement("div");
      textDiv.className = "text-sm font-body font-medium";
      textDiv.textContent = content;
      bubble.appendChild(textDiv);
    }

    div.appendChild(bubble);
    this.messagesTarget.appendChild(div);
    this.scrollToBottom();

    // Trigger motion animations
    document.dispatchEvent(new Event("turbo:load"));
  }

  getSparklesIconSVG() {
    // Inline sparkles SVG (can be replaced with actual svg_icon in Phase 2)
    return `<svg class="h-5 w-5" viewBox="0 0 576 512" style="fill: var(--color-icon-info);">
      <path d="M327.5 85.2c-4.5 1.7-7.5 6-7.5 10.8s3 9.1 7.5 10.8L384 128l21.2 56.5c1.7 4.5 6 7.5 10.8 7.5s9.1-3 10.8-7.5L448 128l56.5-21.2c4.5-1.7 7.5-6 7.5-10.8s-3-9.1-7.5-10.8L448 64 426.8 7.5C425.1 3 420.8 0 416 0s-9.1 3-10.8 7.5L384 64 327.5 85.2zM205.1 73.3c-2.6-5.7-8.3-9.3-14.5-9.3s-11.9 3.6-14.5 9.3L123.3 187.7 9.3 240.5C3.6 243.1 0 248.8 0 255s3.6 11.9 9.3 14.5l114 52.8 52.8 114c2.6 5.7 8.3 9.3 14.5 9.3s11.9-3.6 14.5-9.3l52.8-114 114-52.8c5.7-2.6 9.3-8.3 9.3-14.5s-3.6-11.9-9.3-14.5l-114-52.8L205.1 73.3z"/>
    </svg>`;
  }

  showLoading() {
    const div = document.createElement("div");
    div.className = "flex justify-start mb-4";
    div.dataset.loading = "true";

    const bubble = document.createElement("div");
    bubble.className =
      "max-w-[80%] px-5 py-3 ai-chat-bubble-loading rounded-radius shadow-default";

    const wrapper = document.createElement("div");
    wrapper.className = "flex items-center gap-3";

    const iconDiv = document.createElement("div");
    iconDiv.className = "shrink-0 loading-animation";
    iconDiv.innerHTML = this.getSparklesIconSVG();

    const dotsDiv = document.createElement("div");
    dotsDiv.className = "flex space-x-2";
    dotsDiv.innerHTML = `
      <div class="w-2 h-2 rounded-full animate-bounce" style="background-color: var(--color-on-surface-muted);"></div>
      <div class="w-2 h-2 rounded-full animate-bounce" style="background-color: var(--color-on-surface-muted); animation-delay: 0.15s;"></div>
      <div class="w-2 h-2 rounded-full animate-bounce" style="background-color: var(--color-on-surface-muted); animation-delay: 0.3s;"></div>
    `;

    wrapper.appendChild(iconDiv);
    wrapper.appendChild(dotsDiv);
    bubble.appendChild(wrapper);
    div.appendChild(bubble);
    this.messagesTarget.appendChild(div);
    this.scrollToBottom();

    // Trigger motion animations
    document.dispatchEvent(new Event("turbo:load"));
  }

  hideLoading() {
    this.messagesTarget
      .querySelectorAll("[data-loading]")
      .forEach((el) => el.remove());
  }

  scrollToBottom() {
    if (this.hasMessagesTarget) {
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight;
    }
  }

  showToolExecutions(executions) {
    const div = document.createElement("div");
    div.className = "flex justify-start mb-4";

    const bubble = document.createElement("div");
    bubble.className =
      "max-w-[80%] px-5 py-3 ai-chat-bubble-tools rounded-radius shadow-default";

    const content = document.createElement("div");
    content.className = "text-xs font-body text-on-surface-muted italic";

    executions.forEach((exec, index) => {
      const status = exec.success ? "✓" : "✗";
      const verb = this.toolToVerb(exec.tool);
      const line = document.createElement("div");
      line.textContent = `${status} ${verb}`;
      content.appendChild(line);
    });

    bubble.appendChild(content);
    div.appendChild(bubble);
    this.messagesTarget.appendChild(div);
    this.scrollToBottom();
  }

  toolToVerb(toolName) {
    // Convert tool names to user-friendly verbs
    const verbMap = {
      // Guests
      add_guest: "Adding guest",
      update_guest: "Updating guest",
      list_guests: "Listing guests",
      send_guest_invite: "Sending guest invite",
      delete_guest: "Deleting guest",

      // Timeline
      add_timeline_item: "Adding task",
      update_timeline_item: "Updating task",
      list_timeline_items: "Listing tasks",
      delete_timeline_item: "Deleting task",

      // Collaborators
      add_collaborator: "Adding collaborator",
      update_collaborator: "Updating collaborator",
      list_collaborators: "Listing collaborators",
      send_collaborator_invite: "Sending collaborator invite",
      delete_collaborator: "Removing collaborator",

      // Party
      create_party: "Creating party",
      get_party: "Getting party details",
      update_party: "Updating party",
      list_parties: "Listing parties",
      update_party_plan: "Updating party plan",

      // Media
      list_party_images: "Listing images",
      add_party_image: "Adding image",
      delete_party_image: "Deleting image",
    };

    return verbMap[toolName] || toolName.replace(/_/g, " ");
  }

  handleKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage(event);
    } else if (event.key === "Escape") {
      if (this.state === "conversation") {
        this.minimize();
      } else if (this.state === "input") {
        this.close();
      }
    }
  }
}
